import { useQueryClient } from '@tanstack/react-query'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { Eye, EyeOff, Info, Plus, Save, Trash2, X } from 'lucide-react'
import { useEffect, useState, type FormEvent } from 'react'
import { toast } from 'sonner'

import {
  useChannelSettings,
  useCreateChannel,
  useDeleteChannel,
  useUpdateChannel,
  useUpdateChannelSettings,
  type Channel,
} from '@/api/endpoints/channels'
import { instagramDestination, useChannelDestinations } from '@/api/endpoints/destinations'
import { queryKeys } from '@/api/queryKeys'
import type { ChannelsSearch } from '@/app/routes/_search'
import { Button } from '@/components/ui/Button'
import { Callout } from '@/components/ui/Callout'
import { Chip } from '@/components/ui/Chip'
import { DialogClose, DialogContent, DialogRoot } from '@/components/ui/Dialog'
import { IconButton } from '@/components/ui/IconButton'
import { Input } from '@/components/ui/Input'
import { Segmented, type SegmentedOption } from '@/components/ui/Segmented'
import { EmptyState } from '@/components/ds/EmptyState'
import { PageHeader } from '@/components/ds/PageHeader'
import { Section } from '@/components/ds/Section'
import { PlatformsSection } from '@/features/platforms/PlatformsSection'
import { errorMessage } from '@/lib/errors'
import { useActiveChannelFromUrl } from '@/hooks/useActiveChannel'
import styles from './ChannelsPage.module.scss'
import { AvatarPanel, HandleStatusLine, SuggestPanel, type AvatarBrief } from './ChannelAssist'
import {
  confirmedNotice,
  connectionCheck,
  hasOAuthCallback,
  oauthCallbackNotice,
  type OAuthNotice,
} from './oauthResult'
import {
  VIDEO_BRAND_OPTIONS,
  backendKnowsVideoBrand,
  savedAsSent,
  savedVideoBrandMode,
  videoBrandHelp,
  type VideoBrandMode,
} from './videoBrand'

const DISCORD_WEBHOOK_REGEX = /^https:\/\/(canary\.|ptb\.)?discord(?:app)?\.com\/api\/webhooks\/\d+\/[\w-]+$/

/**
 * Channel detail/edit page. Shows everything tied to the currently
 * selected channel (via the sidebar switcher) — basic info, YouTube OAuth,
 * Discord webhook, and a danger zone for deletion. The user changes which
 * channel they're editing by clicking another in the sidebar.
 *
 * "Novo canal" sits in the page header so creating a new channel doesn't
 * require leaving this page first.
 */
export function ChannelsPage() {
  const { active, channels, isLoading } = useActiveChannelFromUrl()
  const [creating, setCreating] = useState(false)
  const callback = useOAuthCallbackNotice()

  return (
    <div className={styles.page}>
      <PageHeader
        eyebrow="Conta"
        title="Canais"
        description="Edite o canal selecionado no sidebar. Cada canal mantém seus próprios uploads, vídeos, ranking e configurações."
        actions={
          <Button
            size="sm"
            leadingIcon={<Plus />}
            onClick={() => {
              setCreating(true)
            }}
          >
            Novo canal
          </Button>
        }
      />

      <span className={styles.contextHint}>
        <Info /> Editando o canal escolhido no sidebar — clique em outro canal pra trocar.
      </span>

      {callback.notice && (!callback.channel || callback.channel === active?.slug) ? (
        <OAuthCallbackCallout notice={callback.notice} onDismiss={callback.dismiss} />
      ) : null}

      {isLoading ? (
        <p className={styles.loading}>Carregando canais...</p>
      ) : channels.length === 0 || !active ? (
        <Section>
          <EmptyState
            title="Sem canais cadastrados"
            description="Crie o primeiro pra começar."
            action={
              <Button
                leadingIcon={<Plus />}
                onClick={() => {
                  setCreating(true)
                }}
              >
                Novo canal
              </Button>
            }
          />
        </Section>
      ) : (
        <ChannelDetail channel={active} isOnlyChannel={channels.length === 1} />
      )}

      <ChannelDialog
        open={creating}
        mode="create"
        onClose={() => {
          setCreating(false)
        }}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Volta do OAuth — o desfecho vem na URL e sai dela depois de lido
// ---------------------------------------------------------------------------

interface OAuthCallbackState {
  notice: OAuthNotice | null
  /** O canal do callback: a mensagem some ao trocar de canal no sidebar. */
  channel: string | undefined
  dismiss: () => void
}

/**
 * Lê ``platform``/``result`` (e o ``error`` legado) da URL, guarda a mensagem
 * e tira os três da URL — um reload ou um link copiado não repetem o aviso.
 *
 * O "conectado" não se prova pela URL (qualquer um monta o link): ele relê o
 * destino do canal e só aparece com a conta ativa numa leitura posterior à
 * volta do callback.
 */
function useOAuthCallbackNotice(): OAuthCallbackState {
  const search: ChannelsSearch = useSearch({ strict: false })
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [state, setState] = useState<{ notice: OAuthNotice | null; channel: string | undefined; at: number }>({
    notice: null,
    channel: undefined,
    at: 0,
  })
  const pending = hasOAuthCallback(search)
  const { channel, platform, result, error } = search

  useEffect(() => {
    if (!pending) return
    const params: ChannelsSearch = {
      ...(platform ? { platform } : {}),
      ...(result ? { result } : {}),
      ...(error ? { error } : {}),
    }
    const notice = oauthCallbackNotice(params)
    // Sem canal não há o que reler: um "conectado" assim não aparece.
    const verifiable = notice?.verify ? Boolean(channel) : true
    if (notice?.verify && channel) {
      void qc.invalidateQueries({ queryKey: queryKeys.scheduler.destinations(channel) })
    }
    setState({ notice: verifiable ? notice : null, channel, at: Date.now() })
    void navigate({ to: '/channels', search: channel ? { channel } : {}, replace: true })
  }, [pending, channel, platform, result, error, navigate, qc])

  const verifySlug = state.notice?.verify ? (state.channel ?? '') : ''
  const verify = useChannelDestinations(verifySlug, verifySlug !== '')
  const fresh =
    !verify.isFetching && (verify.dataUpdatedAt >= state.at || verify.errorUpdatedAt >= state.at)
  const check = connectionCheck(fresh, instagramDestination(verify.data)?.account?.status)

  return {
    notice: state.notice ? confirmedNotice(state.notice, check) : null,
    channel: state.channel,
    dismiss: () => {
      setState({ notice: null, channel: undefined, at: 0 })
    },
  }
}

function OAuthCallbackCallout({ notice, onDismiss }: { notice: OAuthNotice; onDismiss: () => void }) {
  return (
    <Callout
      role="alert"
      variant={notice.tone === 'success' ? 'neutral' : notice.tone}
      title={notice.title}
      action={
        <IconButton variant="ghost" size="sm" aria-label="Fechar aviso" onClick={onDismiss}>
          <X />
        </IconButton>
      }
    >
      <p>{notice.body}</p>
    </Callout>
  )
}

// ---------------------------------------------------------------------------
// Channel detail (4 sections wrapping the active channel)
// ---------------------------------------------------------------------------

interface ChannelDetailProps {
  channel: Channel
  isOnlyChannel: boolean
}

function ChannelDetail({ channel, isOnlyChannel }: ChannelDetailProps) {
  const [editingDialog, setEditingDialog] = useState(false)
  const [deleting, setDeleting] = useState(false)

  return (
    <>
      <BasicInfoSection channel={channel} onOpenEdit={() => { setEditingDialog(true) }} />
      <VideoBrandSection channel={channel} />
      <PlatformsSection channel={channel} />
      <DiscordWebhookSection channel={channel} />
      <DangerZoneSection
        channel={channel}
        isOnlyChannel={isOnlyChannel}
        onDelete={() => { setDeleting(true) }}
      />

      <ChannelDialog
        open={editingDialog}
        mode="edit"
        channel={channel}
        onClose={() => {
          setEditingDialog(false)
        }}
      />
      {deleting ? (
        <DeleteChannelDialog
          channel={channel}
          onClose={() => {
            setDeleting(false)
          }}
        />
      ) : null}
    </>
  )
}

// ---------------------------------------------------------------------------
// 1. Basic info
// ---------------------------------------------------------------------------

function BasicInfoSection({ channel, onOpenEdit }: { channel: Channel; onOpenEdit: () => void }) {
  return (
    <Section
      title="Informações básicas"
      description="Nome de exibição, tag pública e flag de canal padrão."
      actions={
        <Button size="sm" variant="secondary" onClick={onOpenEdit}>
          Editar
        </Button>
      }
    >
      <div className={styles.form}>
        <ReadOnlyField label="Nome">{channel.name}</ReadOnlyField>
        <ReadOnlyField label="Slug">
          <code>{channel.slug}</code>{' '}
          <span className={styles.hint}>· imutável</span>
        </ReadOnlyField>
        <ReadOnlyField label="Channel tag">
          {channel.channel_tag || <span className={styles.hint}>(vazio)</span>}
        </ReadOnlyField>
        <ReadOnlyField label="Default">
          {channel.is_default ? (
            <Chip variant="primary">Sim</Chip>
          ) : (
            <span className={styles.hint}>Não</span>
          )}
        </ReadOnlyField>
      </div>
    </Section>
  )
}

function ReadOnlyField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className={styles.field}>
      <span className={styles.fieldLabel}>{label}</span>
      <span>{children}</span>
    </div>
  )
}

// ---------------------------------------------------------------------------
// 1b. Marca no vídeo — what the watermark, the cover and the end card carry
// ---------------------------------------------------------------------------

function VideoBrandSection({ channel }: { channel: Channel }) {
  const settings = useChannelSettings(channel.slug)
  const update = useUpdateChannelSettings()

  const saved = savedVideoBrandMode(settings.data)
  const [draft, setDraft] = useState<VideoBrandMode>(saved)

  useEffect(() => {
    if (settings.data) setDraft(savedVideoBrandMode(settings.data))
  }, [settings.data])

  const dirty = draft !== saved
  const help = videoBrandHelp(draft, {
    name: channel.name,
    channel_tag: settings.data?.channel_tag ?? channel.channel_tag,
  })

  function handleSave() {
    update.mutate(
      { slug: channel.slug, patch: { video_brand_mode: draft } },
      {
        onSuccess: (data) => {
          if (savedAsSent(data, draft)) toast.success('Marca no vídeo salva')
          else toast.error('O servidor ainda não conhece essa opção. Tente de novo depois do deploy.')
        },
        onError: (err) => {
          toast.error(errorMessage(err, 'Falha ao salvar a marca no vídeo.'))
        },
      },
    )
  }

  // Front novo com backend antigo (o Pages publica no merge, o skab só no
  // pull): sem o campo na resposta, nada de seletor nem de "salvo" falso.
  if (settings.data && !backendKnowsVideoBrand(settings.data)) return null

  return (
    <Section
      title="Marca no vídeo"
      description="O que fica gravado nos vídeos novos: a marca d'água, a capa e o cartão final. Os vídeos já gerados não mudam."
    >
      <div className={styles.brandRow}>
        <div className={styles.field}>
          <Segmented
            value={draft}
            onValueChange={(v) => {
              setDraft(v)
            }}
            options={VIDEO_BRAND_OPTIONS}
            aria-label="Marca no vídeo"
          />
          <span className={styles.hint} aria-live="polite">
            {help}
          </span>
        </div>
        <Button
          size="sm"
          leadingIcon={<Save />}
          onClick={handleSave}
          disabled={!dirty || update.isPending || settings.isPending}
        >
          {update.isPending ? 'Salvando...' : 'Salvar'}
        </Button>
      </div>
    </Section>
  )
}

// ---------------------------------------------------------------------------
// 2. Plataformas — features/platforms/PlatformsSection (YouTube + destinos)
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// 3. Discord webhook
// ---------------------------------------------------------------------------

function DiscordWebhookSection({ channel }: { channel: Channel }) {
  const settings = useChannelSettings(channel.slug)
  const update = useUpdateChannelSettings()

  const [draft, setDraft] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [showWebhook, setShowWebhook] = useState(false)

  useEffect(() => {
    if (settings.data) setDraft(settings.data.discord_webhook_url)
  }, [settings.data])

  const current = settings.data?.discord_webhook_url ?? ''
  const dirty = draft.trim() !== current.trim()

  function handleSave() {
    const value = draft.trim()
    if (value && !DISCORD_WEBHOOK_REGEX.test(value)) {
      setError('URL inválida. Cole o link completo do webhook do Discord.')
      return
    }
    setError(null)
    update.mutate(
      { slug: channel.slug, patch: { discord_webhook_url: value } },
      {
        onSuccess: () => {
          toast.success(value ? 'Webhook salvo' : 'Webhook removido')
        },
        onError: (err) => {
          toast.error(errorMessage(err, 'Falha ao salvar webhook.'))
        },
      },
    )
  }

  return (
    <Section
      title="Notificações Discord"
      description="Webhook que recebe avisos de pipeline (sucesso, falha, custo elevado, lentidão). Vazio cai no webhook ops global do .env."
    >
      <div className={styles.discordRow}>
        <div className={styles.field}>
          <div className={styles.webhookInputRow}>
            <Input
              type={showWebhook ? 'text' : 'password'}
              aria-label="Discord webhook"
              placeholder="https://discord.com/api/webhooks/..."
              value={draft}
              disabled={settings.isPending}
              autoComplete="off"
              spellCheck={false}
              onChange={(e) => {
                setDraft(e.target.value)
                setError(null)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && dirty && !update.isPending) handleSave()
              }}
            />
            <Button
              variant="ghost"
              size="sm"
              aria-label={showWebhook ? 'Ocultar webhook' : 'Mostrar webhook'}
              aria-pressed={showWebhook}
              onClick={() => { setShowWebhook((s) => !s) }}
              leadingIcon={showWebhook ? <EyeOff /> : <Eye />}
            />
          </div>
          {error ? <span className={styles.errorText}>{error}</span> : null}
        </div>
        <Button
          size="sm"
          leadingIcon={<Save />}
          onClick={handleSave}
          disabled={!dirty || update.isPending || settings.isPending}
        >
          {update.isPending ? 'Salvando...' : 'Salvar'}
        </Button>
      </div>
    </Section>
  )
}

// ---------------------------------------------------------------------------
// 4. Danger zone
// ---------------------------------------------------------------------------

function DangerZoneSection({
  channel,
  isOnlyChannel,
  onDelete,
}: {
  channel: Channel
  isOnlyChannel: boolean
  onDelete: () => void
}) {
  return (
    <Section
      title="Danger zone"
      description="Ações destrutivas. Não há undo."
      className={styles.danger}
    >
      <p className={styles.note}>
        Remover este canal apaga vídeos, uploads, agendas, ranking e settings do banco. Os arquivos
        em <code>output/{channel.slug}/</code> ficam intocados.
      </p>
      <div className={styles.dangerActions}>
        <Button
          variant="primary"
          leadingIcon={<Trash2 />}
          onClick={onDelete}
          disabled={isOnlyChannel}
        >
          Remover canal
        </Button>
        {isOnlyChannel ? (
          <span className={styles.note}>
            Não é possível remover o último canal restante.
          </span>
        ) : null}
      </div>
    </Section>
  )
}

// ---------------------------------------------------------------------------
// Create / edit dialog (shared)
// ---------------------------------------------------------------------------

interface ChannelDialogProps {
  open: boolean
  mode: 'create' | 'edit'
  channel?: Channel
  onClose: () => void
}

type Platform = 'youtube' | 'tiktok'

const PLATFORM_OPTIONS: SegmentedOption[] = [
  { value: 'youtube', label: 'YouTube' },
  { value: 'tiktok', label: 'TikTok' },
]

function ChannelDialog({ open, mode, channel, onClose }: ChannelDialogProps) {
  const create = useCreateChannel()
  const update = useUpdateChannel()

  const [name, setName] = useState(channel?.name ?? '')
  const [tag, setTag] = useState(channel?.channel_tag ?? '')
  const [isDefault, setIsDefault] = useState(channel?.is_default ?? false)
  const [platform, setPlatform] = useState<Platform>(channel?.platform ?? 'youtube')
  const [error, setError] = useState<string | null>(null)
  const [description, setDescription] = useState('')
  const [brief, setBrief] = useState<AvatarBrief | null>(null)

  useEffect(() => {
    if (open) {
      setName(channel?.name ?? '')
      setTag(channel?.channel_tag ?? '')
      setIsDefault(channel?.is_default ?? false)
      setPlatform(channel?.platform ?? 'youtube')
      setError(null)
      setDescription('')
      setBrief(null)
      create.reset()
      update.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, channel?.slug])

  const pending = mode === 'create' ? create.isPending : update.isPending

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    const trimmed = name.trim()
    if (!trimmed) {
      setError('Nome é obrigatório')
      return
    }
    setError(null)
    if (mode === 'create') {
      create.mutate(
        {
          name: trimmed,
          channel_tag: tag.trim() || null,
          is_default: isDefault,
          platform,
        },
        {
          onSuccess: (data) => {
            toast.success(`Canal "${data.name}" criado`)
            onClose()
          },
          onError: (err) => {
            toast.error(errorMessage(err, 'Falha ao criar canal'))
          },
        },
      )
    } else if (channel) {
      update.mutate(
        {
          slug: channel.slug,
          patch: { name: trimmed, channel_tag: tag.trim() || null, is_default: isDefault },
        },
        {
          onSuccess: () => {
            toast.success('Canal atualizado')
            onClose()
          },
          onError: (err) => {
            toast.error(errorMessage(err, 'Falha ao atualizar canal'))
          },
        },
      )
    }
  }

  return (
    <DialogRoot
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent
        className={mode === 'create' ? styles.createDialog : undefined}
        title={mode === 'create' ? 'Novo canal' : `Editar · ${channel?.name ?? ''}`}
        description={
          mode === 'create'
            ? 'Descreva o canal para a IA sugerir nome, @ e foto, ou preencha à mão. O canal em si nasce no YouTube (a API não permite criar); o slug sai do nome e não muda depois.'
            : 'O slug é imutável. Pra renomear o canal mantenha o slug original e edite só o nome.'
        }
        footer={
          <>
            <DialogClose asChild>
              <Button variant="secondary" disabled={pending}>
                Cancelar
              </Button>
            </DialogClose>
            <Button type="submit" form="channel-dialog-form" disabled={pending}>
              {pending ? 'Salvando...' : 'Salvar'}
            </Button>
          </>
        }
      >
        <form id="channel-dialog-form" className={styles.dialogForm} onSubmit={onSubmit} noValidate>
          {mode === 'create' ? (
            <div className={styles.field}>
              {/* <span>, não <label>: o Segmented não renderiza um controle
                  nativo para associar via htmlFor, e ele já carrega o
                  aria-label. Um <label> solto não rotula nada — só parecia
                  acessível. */}
              <span className={styles.fieldLabel}>Plataforma</span>
              <Segmented
                value={platform}
                onValueChange={(v) => { setPlatform(v as Platform) }}
                options={PLATFORM_OPTIONS}
                aria-label="Plataforma"
              />
              <span className={styles.hint}>
                Imutável depois de criar. Cada plataforma usa OAuth e tabelas próprias.
              </span>
            </div>
          ) : null}
          {mode === 'create' ? (
            <SuggestPanel
              platform={platform}
              description={description}
              onDescriptionChange={setDescription}
              currentHandle={tag}
              onBrief={setBrief}
              onPick={(s) => {
                setName(s.name)
                setTag(`@${s.handle}`)
                setError(null)
              }}
            />
          ) : null}
          <div className={styles.field}>
            <label className={styles.fieldLabel} htmlFor="ch-name">Nome</label>
            <Input
              id="ch-name"
              placeholder="Ex: Bug da Realidade"
              value={name}
              onChange={(e) => {
                setName(e.target.value)
                setError(null)
              }}
            />
            {error ? <span className={styles.errorText}>{error}</span> : null}
          </div>
          <div className={styles.field}>
            <label className={styles.fieldLabel} htmlFor="ch-tag">
              Channel tag <span className={styles.hint}>· opcional</span>
            </label>
            <Input
              id="ch-tag"
              placeholder="Ex: @bugdarealidade"
              value={tag}
              onChange={(e) => {
                setTag(e.target.value)
              }}
            />
            {mode === 'create' && platform === 'youtube' ? (
              <HandleStatusLine
                tag={tag}
                name={name}
                description={description}
                onPickHandle={(h) => {
                  setTag(`@${h}`)
                }}
              />
            ) : null}
          </div>
          {mode === 'create' ? (
            <AvatarPanel name={name} description={description} brief={brief} onBrief={setBrief} />
          ) : null}
          <label className={styles.checkRow}>
            <input
              type="checkbox"
              checked={isDefault}
              onChange={(e) => {
                setIsDefault(e.target.checked)
              }}
            />
            <span>Tornar este o canal padrão</span>
          </label>
          {mode === 'edit' && channel ? (
            <p className={styles.hint}>
              Slug atual: <code>{channel.slug}</code>
            </p>
          ) : null}
        </form>
      </DialogContent>
    </DialogRoot>
  )
}

// ---------------------------------------------------------------------------
// Delete confirmation
// ---------------------------------------------------------------------------

function DeleteChannelDialog({ channel, onClose }: { channel: Channel; onClose: () => void }) {
  const remove = useDeleteChannel()
  const [confirmName, setConfirmName] = useState('')
  const ok = confirmName.trim() === channel.slug

  return (
    <DialogRoot open onOpenChange={(next) => { if (!next) onClose() }}>
      <DialogContent
        title={`Remover canal · ${channel.name}`}
        description="Essa ação é irreversível. Vídeos, uploads, agendas e settings desse canal serão removidos do banco. Os arquivos em disco (output/) ficam intocados."
        footer={
          <>
            <DialogClose asChild>
              <Button variant="secondary" disabled={remove.isPending}>
                Cancelar
              </Button>
            </DialogClose>
            <Button
              variant="primary"
              disabled={!ok || remove.isPending}
              onClick={() => {
                remove.mutate(channel.slug, {
                  onSuccess: () => {
                    toast.success(`Canal "${channel.name}" removido`)
                    onClose()
                  },
                  onError: (err) => {
                    toast.error(errorMessage(err, 'Falha ao remover canal'))
                  },
                })
              }}
            >
              {remove.isPending ? 'Removendo...' : 'Remover canal'}
            </Button>
          </>
        }
      >
        <div className={styles.dialogForm}>
          <p className={styles.note}>
            Pra confirmar, digite o slug do canal: <code>{channel.slug}</code>
          </p>
          <Input
            placeholder={channel.slug}
            value={confirmName}
            onChange={(e) => {
              setConfirmName(e.target.value)
            }}
          />
        </div>
      </DialogContent>
    </DialogRoot>
  )
}
